/**
 * ComposerScrollRow — the single-line scrolling rail shared by the composer's
 * context row and attachment row.
 *
 * The rule for both rows is the same and non-negotiable: ONE line, horizontal
 * scroll, never wrap. A wrapping row lets five staged files eat ~280px of panel
 * height and push the thread off screen, which is exactly the failure this
 * replaced. Bounded height is the feature.
 *
 * Owns the parts that have to agree between the two rows: hidden scrollbar,
 * the `+N` overflow counter pinned OUTSIDE the scroll area (so it can't scroll
 * away), and a short right-edge mask so a half-visible chip reads as
 * "scrollable" rather than "clipped".
 */

import { Box, HStack, Text, VisuallyHidden } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface ComposerScrollRowProps {
  /** Names the group for screen readers, e.g. "Chat context". */
  readonly ariaLabel: string;
  /** Pinned to the left, outside the scroller — e.g. the `@` button. */
  readonly lead?: ReactNode;
  readonly children: ReactNode;
  /**
   * Singular/plural noun for the counter's visually-hidden phrase. "+3" alone
   * tells a screen-reader user nothing about what is hidden.
   */
  readonly overflowNoun: readonly [singular: string, plural: string];
  /** Re-measure whenever this changes — the item set, in practice. */
  readonly measureKey: unknown;
}

/**
 * Number of items whose right edge sits past the visible right edge of the
 * scroller. Recomputed on scroll, on resize, and whenever the item set changes
 * — a stale counter is worse than no counter.
 *
 * The +1 tolerance is deliberate: an item ending 0.5px past the edge is layout
 * rounding, not something the user can't see, and without it the row renders a
 * permanent phantom "+1".
 */
function countHiddenItems(scroller: HTMLElement): number {
  const visibleRight = scroller.scrollLeft + scroller.clientWidth;
  const items = Array.from(scroller.querySelectorAll<HTMLElement>('[data-composer-chip]'));
  return items.filter((item) => item.offsetLeft + item.offsetWidth > visibleRight + 1).length;
}

export function ComposerScrollRow({
  ariaLabel,
  lead,
  children,
  overflowNoun,
  measureKey,
}: ComposerScrollRowProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [hiddenCount, setHiddenCount] = useState(0);

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setHiddenCount(countHiddenItems(el));
  }, []);

  // Layout effect so the counter is right on the first paint after an item is
  // added — a one-frame flash of "+1" that immediately disappears reads as a bug.
  useLayoutEffect(measure, [measure, measureKey]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <HStack spacing={1} mb={2} align="center" role="group" aria-label={ariaLabel}>
      {lead}

      <Box position="relative" flex={1} minW={0}>
        <HStack
          ref={scrollerRef}
          onScroll={measure}
          // MUST stay positioned: countHiddenItems compares item.offsetLeft
          // against this element's scroll box, and offsetLeft is measured from
          // the nearest POSITIONED ancestor. Static here means every item's
          // offsetLeft carries the scroller's own offset inside the panel as a
          // constant bias, and the counter over-reports.
          position="relative"
          spacing={1}
          // The whole point of the rail. Wrapping is what this replaced.
          flexWrap="nowrap"
          overflowX="auto"
          overflowY="hidden"
          py="1px"
          sx={{
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {children}
        </HStack>

        {hiddenCount > 0 && (
          // Fades the clipped edge into the composer surface. Decorative and
          // non-interactive, so it must not swallow clicks on the chip beneath.
          <Box
            aria-hidden
            position="absolute"
            top={0}
            bottom={0}
            right={0}
            w="16px"
            pointerEvents="none"
            bgGradient="linear(to-r, transparent, bg.surface)"
          />
        )}
      </Box>

      {hiddenCount > 0 && (
        // `aria-label` on a bare Text isn't reliably announced, so the full
        // phrase is real (visually hidden) text and the glyph is decorative.
        <Text flexShrink={0} fontSize="2xs" fontWeight="medium" color="text.muted" px={1}>
          <Box as="span" aria-hidden>
            +{hiddenCount}
          </Box>
          <VisuallyHidden>
            {hiddenCount} more {hiddenCount === 1 ? overflowNoun[0] : overflowNoun[1]}, scroll to
            see
          </VisuallyHidden>
        </Text>
      )}
    </HStack>
  );
}
